import * as zod from 'zod';

import {
  ABSOLUTE_MAX_JOINT_ACCELERATIONS,
  ABSOLUTE_MAX_JOINT_SPEEDS,
} from '@sb/motion-planning';
import { Step } from '@sb/remote-control/types';
import {
  DEFAULT_SERVO_CONFIG,
  ExternalControlMotionScalingLimits,
  ExternalControlReferenceFrame,
  ExternalControlServoConfig,
  type ResolvedServoConfig as ResolvedServoConfigBase,
} from '@sb/routine-runner/Step/ExternalControl/Arguments';

export namespace ExternalControlStep {
  export const name = 'External Control';
  export const description =
    'Request control source for external control, run substeps, then release control source';
  export const isDecorator = true;
  export const librarySection = Step.LibrarySection.Control;
  export const librarySort = '1';
  export const argumentKind = 'ExternalControlStep';

  export const featureFlag = 'externalControlStep';

  /** Streaming modality the session expects. Mirrors the routine-runner
   * ExternalControl `streamType` and drives which control source / topic the
   * step uses (pose, joint streaming on stream_joints, or jointTrajectory via
   * the scaled_joint_trajectory_controller). `joints` and `pose` route
   * through the trajectory controller under `onlinePlannerStreaming`. */
  export const StreamType = zod.enum(['pose', 'joints', 'jointTrajectory']);
  export type StreamType = zod.infer<typeof StreamType>;

  /** Human-facing labels for the stream-type dropdown in the step editor. */
  export const StreamTypeLabels: Record<StreamType, string> = {
    pose: 'Pose',
    joints: 'Joints',
    jointTrajectory: 'Joint Trajectory',
  };

  /** Short, operator-facing explanation of each stream type, shown under the
   * dropdown. Describes what gets streamed, not how it is routed internally. */
  export const StreamTypeDescriptions: Record<StreamType, string> = {
    pose: 'Stream Cartesian poses to the robot.',
    joints: 'Stream joint positions to the robot.',
    jointTrajectory: 'Send a joint trajectory for the robot to follow.',
  };

  /** How the external client takes over. Mirrors the routine-runner
   * `controlledBy`. `api`, `rosHumble`, and `rosJazzy` are all exposed in the
   * UI; the external-control-bot maps each ROS distro to the matching bridge
   * conda env. */
  export const ControlledBy = zod.enum(['api', 'rosHumble', 'rosJazzy']);
  export type ControlledBy = zod.infer<typeof ControlledBy>;

  /** Human-facing labels for the "Controlled by" dropdown. */
  export const ControlledByLabels: Record<ControlledBy, string> = {
    api: 'API',
    rosHumble: 'ROS Humble',
    rosJazzy: 'ROS Jazzy',
  };

  /**
   * Optional trajectory-controller servo tuning, applied when the session
   * streams joints (ServoJ) or poses (ServoL) through the online planner
   * under `onlinePlannerStreaming`. Omitted fields fall back to the server
   * default.
   * Temporary surface while the online-planner streaming API stabilizes.
   *
   * The schema is owned by the routine-runner step (the layer that validates
   * it at load); re-exported here so every producer parses the same shape.
   */
  export const ServoConfig = ExternalControlServoConfig;
  export type ServoConfig = zod.infer<typeof ServoConfig>;

  /** ServoL's optional per-joint caps — both vectors, or absent. */
  export const MotionScalingLimits = ExternalControlMotionScalingLimits;
  export type MotionScalingLimits = zod.infer<typeof MotionScalingLimits>;

  /** A servo config with the scalar defaults filled in; `motionScalingLimits`
   * stays optional (absent = no per-joint limit). */
  export type ResolvedServoConfig = ResolvedServoConfigBase;

  /** Server-side servo defaults, shared by ServoJ and ServoL; seeds the
   * Streaming settings UI so its controls start from the values an omitted
   * field would fall back to. */
  export const ServoConfigDefaults: ResolvedServoConfig = DEFAULT_SERVO_CONFIG;

  /**
   * Operator-facing explanation of `positionSmoothingTimeConstantMs`, shown
   * behind the info icon next to the field.
   */
  export const PositionSmoothingHint =
    'Time constant for smoothing the jump when a streamed position command ' +
    'differs from the signal extrapolated from the last position and ' +
    'velocity. The difference decays exponentially at this rate instead of ' +
    'being applied in a single control cycle. 0 applies it immediately.';

  /**
   * Operator-facing explanation of the commanded point, shown behind the info
   * icon beside it.
   */
  export const CommandedPointHint =
    'A streamed pose commands the robot’s Active Tooltip — the same point ' +
    'jogging moves. Change it from the Move page, with the Tooltip control ' +
    'beside the jog pad.';

  export const ReferenceFrame = ExternalControlReferenceFrame;
  export type ReferenceFrame = zod.infer<typeof ReferenceFrame>;

  /** Human-facing labels for the reference-frame dropdown. */
  export const ReferenceFrameLabels: Record<ReferenceFrame, string> = {
    base: 'Robot base',
    world: 'World',
  };

  /** Operator-facing explanation, shown under the dropdown. */
  export const ReferenceFrameDescriptions: Record<ReferenceFrame, string> = {
    base: 'Poses are relative to the robot base.',
    world:
      'Poses are relative to the world, applying the custom base placement.',
  };

  /** Per-joint row labels for the ServoL motion-limit grid, J0–J5. */
  export const JointLimitLabels = ['J0', 'J1', 'J2', 'J3', 'J4', 'J5'];

  /** Seeds the toggle's grid at the per-joint hardware ceiling, so enabling it
   * changes nothing until a cell is lowered. */
  export const ServoConfigJointLimitSeed: Readonly<{
    jointVelocityMax: readonly number[];
    jointAccelerationMax: readonly number[];
  }> = {
    jointVelocityMax: [...ABSOLUTE_MAX_JOINT_SPEEDS],
    jointAccelerationMax: [...ABSOLUTE_MAX_JOINT_ACCELERATIONS],
  };

  /** Operator-facing explanation of the per-joint ServoL limits, shown behind
   * the info icon beside the grid. */
  export const JointLimitsHint =
    'Caps each joint’s speed and acceleration while streaming poses. Turn off ' +
    'for no per-joint limit. All six of both apply together, or neither.';

  /** Arguments to be passed to the routine runner */
  export const Arguments = zod.object({
    argumentKind: zod.literal(argumentKind),
    streamType: StreamType.default('pose'),
    controlledBy: ControlledBy.default('api'),
    servoConfig: ServoConfig.optional(),
  });

  export type Arguments = zod.infer<typeof Arguments>;

  export const toRoutineRunner: Step.ToRoutineRunner = ({
    stepConfiguration: { args },
    stepData,
  }) => {
    if (args?.argumentKind !== argumentKind) {
      throw new TypeError(`Expected argument kind ${argumentKind}`);
    }

    return {
      ...stepData,
      stepKind: 'ExternalControl',
      args: {
        streamType: args.streamType ?? 'pose',
        controlledBy: args.controlledBy ?? 'api',
        servoConfig: args.servoConfig,
      },
    };
  };

  export const getStepDescription: Step.GetStepDescription = ({
    stepConfiguration: { args },
    includeStepName,
  }) => {
    if (args?.argumentKind !== argumentKind) {
      return null;
    }

    return includeStepName ? 'External Control' : '';
  };
}

ExternalControlStep satisfies Step.StepKindInfo;
